import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, ArrowLeft } from "lucide-react";
import {
  FaUserClock,
  FaRobot,
  FaCheckCircle,
  FaShieldAlt,
  FaBolt,
  FaSeedling,
} from "react-icons/fa";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";
import { googleSignupIdentity } from "../api/auth";
import { useGoogleIdentityButton } from "../hooks/useGoogleIdentityButton";

type RegisterForm = {
  email: string;
  password: string;
  role: string;
  first_name: string;
  last_name: string;
  mobile: string;
  company_name?: string;
  plan: "starter" | "professional" | "enterprise";
};

function scorePassword(pw: string): { score: number; label: string } {
  if (!pw) return { score: 0, label: "Empty" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ["Too short", "Weak", "Fair", "Strong", "Excellent"];
  return { score, label: labels[score] };
}

export function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  // Signing up with Google. The ticket stands in for the password — Google
  // has vouched for the address, so the account is created without one. It
  // cannot replace the rest of the form: Google knows neither a mobile number
  // nor a company name, and registration provisions the whole workspace.
  const [googleTicket, setGoogleTicket] = useState<string | null>(null);
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const { toast } = useToast();
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  // Self-serve registration is Starter-only. Professional/Enterprise go through
  // the Contact Us flow; plan upgrades are handled from the backend for now.
  const { register, handleSubmit, setValue } = useForm<RegisterForm>({
    defaultValues: { role: "admin", plan: "starter" },
  });

  const strength = useMemo(() => scorePassword(passwordValue), [passwordValue]);

  // Arrow, not the function itself: the handler is declared below, and naming
  // it here directly would read it before it exists.
  const googleButtonRef = useGoogleIdentityButton({
    clientId: GOOGLE_CLIENT_ID,
    enabled: true,
    text: "signup_with",
    onCredential: (credential) => handleGoogleCredential(credential),
  });

  const handleGoogleCredential = async (credential?: string) => {
    if (!credential) return;
    try {
      setLoading(true);
      const identity = await googleSignupIdentity(credential);

      if (identity.workspace_exists) {
        // register() refuses a second workspace on the same email, so say so
        // now rather than after the whole form has been filled in.
        toast({
          variant: "destructive",
          description: "This Google account already has a workspace. Sign in instead.",
        });
        navigate("/login");
        return;
      }

      setGoogleEmail(identity.email);
      setGoogleTicket(identity.ticket);
      setValue("email", identity.email);
      if (identity.first_name) setValue("first_name", identity.first_name);
      if (identity.last_name) setValue("last_name", identity.last_name);
      toast({
        description: `Continuing as ${identity.email}. Just your company details left.`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        description: (error as Error)?.message || "Google sign-up failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: RegisterForm) => {
    try {
      setLoading(true);
      await registerUser(
        data.email,
        data.password,
        data.role,
        data.first_name,
        data.last_name,
        data.mobile,
        data.company_name,
        data.plan,
        googleTicket ?? undefined
      );
      toast({
        title: "Success",
        description: "Account created successfully",
      });
      navigate("/login");
    } catch (error) {
      toast({
        variant: "destructive",
        description: (error as any)?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordRegister = register("password", { required: !googleTicket });

  return (
    <div
      className="min-h-screen relative overflow-hidden text-[#1a1a1a]"
      style={{
        background: "var(--gt-surface-1, #faf8f3)",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      <style>{`
        @keyframes rg-fade-up { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes rg-pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        @keyframes rg-bar-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .rg-fade { animation: rg-fade-up 0.85s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        .rg-stagger > * { animation: rg-fade-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        .rg-stagger > *:nth-child(1) { animation-delay: 80ms; }
        .rg-stagger > *:nth-child(2) { animation-delay: 200ms; }
        .rg-stagger > *:nth-child(3) { animation-delay: 320ms; }
        .rg-stagger > *:nth-child(4) { animation-delay: 440ms; }
        .rg-stagger > *:nth-child(5) { animation-delay: 560ms; }
        .rg-stagger > *:nth-child(6) { animation-delay: 680ms; }
        .rg-pulse { animation: rg-pulse-dot 1.6s ease-in-out infinite; }
        .rg-grain {
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.05 0'/></filter><rect width='240' height='240' filter='url(%23n)'/></svg>");
        }
        .rg-serif { font-family: 'Fraunces', 'Iowan Old Style', Georgia, serif !important; }
        .rg-mono { font-family: 'JetBrains Mono', ui-monospace, monospace !important; }
        .rg-eyebrow { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 500; }
        .rg-input { background: transparent; border: 0; border-bottom: 2px solid #d6d0c2; outline: none; transition: border-color 200ms; font-family: 'Fraunces', Georgia, serif; font-size: 22px; color: #0f2d1f; padding: 10px 0; width: 100%; }
        .rg-input::placeholder { color: #b8b3ac; font-style: italic; }
        .rg-input:focus { border-bottom-color: #0f2d1f; }
        .rg-press { transition: transform 200ms cubic-bezier(0.33, 1, 0.68, 1), background 200ms; }
        .rg-press:hover { transform: translateY(-2px); }
        .rg-press:active { transform: translateY(0); }
        .rg-link-arrow { transition: transform 240ms cubic-bezier(0.33, 1, 0.68, 1); }
        .group:hover .rg-link-arrow, button:hover .rg-link-arrow, a:hover .rg-link-arrow { transform: translateX(4px); }
        .rg-bar { transform-origin: left; animation: rg-bar-grow 360ms cubic-bezier(0.22, 1, 0.36, 1); }
        .rg-num-rule {
          background-image: linear-gradient(to right, rgba(15,45,31,0.35) 50%, transparent 50%);
          background-size: 6px 1px;
          background-repeat: repeat-x;
          background-position: left center;
        }
      `}</style>

      <div className="grid lg:grid-cols-12 min-h-screen">
        {/* LEFT — editorial pane (hidden on mobile) */}
        <div className="hidden lg:col-span-7 relative bg-[#0f2d1f] text-[#faf8f3] p-8 lg:p-14 lg:flex flex-col justify-between overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0 rg-grain opacity-20 mix-blend-overlay" />
          <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#1f4030] opacity-50 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-32 w-[460px] h-[460px] rounded-full bg-[#1f4030] opacity-40 blur-3xl" />

          {/* Brand */}
          <button
            onClick={() => navigate("/")}
            className="relative flex items-center gap-3 group rg-fade self-start"
            style={{ animationDelay: "60ms" }}
          >
            <div className="w-9 h-9 rounded-full bg-[#faf8f3] flex items-center justify-center">
              <FaUserClock className="text-[#0f2d1f] text-base" />
            </div>
            <div className="leading-tight text-left">
              <div
                className="rg-serif text-[20px] font-semibold tracking-tight flex items-center gap-2"
                style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#faf8f3" }}
              >
                Teamlio
                <span
                  className="rg-mono inline-flex items-center gap-1 bg-[#1f4030] px-1.5 py-0.5 rounded-full"
                  style={{ fontSize: "8px", letterSpacing: "0.16em", color: "#d4a356" }}
                >
                  <FaRobot className="text-[8px]" /> AI
                </span>
              </div>
              <div className="rg-eyebrow" style={{ fontSize: "9px", color: "#9eb3a4" }}>
                Est. 2024 · India
              </div>
            </div>
          </button>

          {/* Editorial body */}
          <div className="relative max-w-xl">
            <div
              className="flex items-center gap-3 mb-6 flex-wrap rg-fade"
              style={{ animationDelay: "180ms" }}
            >
              <span className="rg-eyebrow" style={{ color: "#9eb3a4" }}>
                Volume 01 — Spring 2026
              </span>
              <span className="h-px w-10 bg-[#9eb3a4]/40" />
              <span className="rg-eyebrow" style={{ color: "#d4a356" }}>
                Chapter 01 · New file
              </span>
            </div>
            <h1
              className="rg-serif rg-fade"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                color: "#faf8f3",
                fontSize: "clamp(36px, 5vw, 60px)",
                lineHeight: 1,
                letterSpacing: "-0.025em",
                fontWeight: 500,
                animationDelay: "300ms",
              }}
            >
              The first signature
              <br />
              <span style={{ fontStyle: "italic", color: "#d4a356" }}>
                is yours to write.
              </span>
            </h1>
            <p
              className="text-[16px] leading-[1.65] mt-6 max-w-md rg-fade"
              style={{ color: "#9eb3a4", animationDelay: "420ms" }}
            >
              Set the workspace your team will gather around for years. Five
              minutes, a few details — we'll tee up the rest before the kettle
              boils.
            </p>

            <ul className="mt-10 space-y-3.5 rg-stagger">
              {[
                { Icon: FaBolt, label: "Up and running in under five minutes", c: "#d4a356" },
                { Icon: FaSeedling, label: "Free for teams of up to five employees", c: "#2f9e6c" },
                { Icon: FaShieldAlt, label: "End-to-end encrypted, hosted in India", c: "#9eb3a4" },
                { Icon: FaCheckCircle, label: "Invite the team after — no card needed today", c: "#faf8f3" },
              ].map((cap, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span
                    className="w-8 h-8 rounded-full bg-[#1f4030] flex items-center justify-center"
                    style={{ color: cap.c }}
                  >
                    <cap.Icon className="text-xs" />
                  </span>
                  <span className="text-[14px]" style={{ color: "#faf8f3" }}>
                    {cap.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pull quote */}
          <div className="relative max-w-xl rg-fade" style={{ animationDelay: "840ms" }}>
            <div
              className="rg-serif"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                color: "#faf8f3",
                fontStyle: "italic",
                fontSize: "clamp(18px, 1.8vw, 22px)",
                lineHeight: 1.35,
                fontWeight: 400,
              }}
            >
              "Set up was the only thing that just worked. Logged in, added
              the team, ran payroll the same afternoon."
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="w-9 h-9 rounded-full bg-[#1f4030] flex items-center justify-center text-[#d4a356] font-semibold text-sm">
                RM
              </div>
              <div className="leading-tight">
                <div className="text-[13px] font-semibold" style={{ color: "#faf8f3" }}>
                  Rohan Mehra
                </div>
                <div className="rg-eyebrow" style={{ fontSize: "9px", color: "#9eb3a4" }}>
                  Founder · Day-one signup, March 2026
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — form pane */}
        <div
          className="lg:col-span-5 relative flex flex-col"
          style={{ background: "var(--gt-surface-1, #faf8f3)" }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-[#e8efe9] opacity-50 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-32 -left-24 w-[360px] h-[360px] rounded-full bg-[#f1ead8] opacity-60 blur-3xl"
          />

          <div className="relative flex-1 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-md rg-fade" style={{ animationDelay: "120ms" }}>
              {/* Top meta row */}
              <div className="flex items-center justify-between mb-8">
                <div className="rg-eyebrow text-[#8a8580]" style={{ fontSize: "10px" }}>
                  Onboarding · Account
                </div>
                <button
                  onClick={() => navigate("/login")}
                  className="rg-mono text-[10px] text-[#0f2d1f] hover:text-[#1f4030] transition-colors inline-flex items-center gap-1.5 group"
                  style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}
                >
                  Have an account
                  <ArrowRight className="w-3 h-3 rg-link-arrow" />
                </button>
              </div>

              {/* Heading */}
              <div className="mb-8">
                <h2
                  className="rg-serif text-[#0f2d1f] mb-3"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: "clamp(28px, 3.4vw, 40px)",
                    lineHeight: 1.05,
                    letterSpacing: "-0.025em",
                    fontWeight: 500,
                  }}
                >
                  Create your{" "}
                  <span style={{ fontStyle: "italic" }}>account.</span>
                </h2>
                <p className="text-[14px] text-[#4a4a4a] leading-[1.6]">
                  You'll be the workspace administrator. Invite the rest of the
                  team once the kettle's on.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-7">
                {/* Sign up with Google — the one-click path, so it leads.
                    Google renders (and size-caps) its own button, so the real
                    one is invisible and stretched over ours below, which
                    matches the Create account button. */}
                {GOOGLE_CLIENT_ID && !googleTicket && (
                  <>
                    <div className="relative rounded-full focus-within:ring-2 focus-within:ring-[#0f2d1f]/25">
                      <div
                        aria-hidden
                        className="w-full inline-flex items-center justify-center gap-3 py-3.5 rounded-full font-medium shadow-[inset_0_0_0_1px_#d6d0c2] bg-[#ffffff] text-[#1a1a1a] rg-press hover:bg-[#f4f1ea] transition-colors"
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                          <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
                          <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
                          <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
                          <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
                        </svg>
                        <span>Sign up with Google</span>
                      </div>
                      <div ref={googleButtonRef} className="absolute inset-0 overflow-hidden opacity-0" />
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="h-px flex-1 bg-[#e8e3d8]" />
                      <span className="rg-mono text-[#8a8580]" style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase" }}>or sign up with email</span>
                      <span className="h-px flex-1 bg-[#e8e3d8]" />
                    </div>
                  </>
                )}

                {googleTicket && (
                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#0f2d1f] bg-[#e8efe9] px-4 py-3">
                    <span className="text-[13px] text-[#0f2d1f]">
                      Signing up with Google as <strong className="font-semibold">{googleEmail}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => { setGoogleTicket(null); setGoogleEmail(null); setValue("email", ""); }}
                      className="rg-mono text-[10px] text-[#0f2d1f] hover:underline shrink-0"
                      style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}
                    >
                      Use a password instead
                    </button>
                  </div>
                )}

                <fieldset>
                  <legend
                    className="rg-eyebrow text-[#8a8580] block mb-3"
                    style={{ fontSize: "10px" }}
                  >
                    Your plan
                  </legend>
                  <input type="hidden" value="starter" {...register("plan")} />
                  <div className="rounded-xl border border-[#0f2d1f] bg-[#e8efe9] p-4">
                    <div className="flex items-center justify-between">
                      <span className="block text-sm font-semibold text-[#0f2d1f]">Starter — Free</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-[#0f2d1f]">Selected</span>
                    </div>
                    <span className="mt-1 block text-[11px] text-[#5a6a5f]">Up to 5 employees. Need Professional or a custom plan? <a href="/contact" className="font-semibold text-[#0f2d1f] underline">Talk to our team</a>.</span>
                  </div>
                </fieldset>

                {/* Name row */}
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="first_name"
                      className="rg-eyebrow text-[#8a8580] block mb-2"
                      style={{ fontSize: "10px" }}
                    >
                      First name
                    </label>
                    <input
                      id="first_name"
                      type="text"
                      placeholder="Anita"
                      autoComplete="given-name"
                      className="rg-input"
                      style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "20px" }}
                      {...register("first_name", { required: true })}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="last_name"
                      className="rg-eyebrow text-[#8a8580] block mb-2"
                      style={{ fontSize: "10px" }}
                    >
                      Last name
                    </label>
                    <input
                      id="last_name"
                      type="text"
                      placeholder="Krishnan"
                      autoComplete="family-name"
                      className="rg-input"
                      style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "20px" }}
                      {...register("last_name", { required: true })}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="rg-eyebrow text-[#8a8580] block mb-2"
                    style={{ fontSize: "10px" }}
                  >
                    Work email
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    className="rg-input"
                    style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                    // Google vouched for this address; letting it be edited
                    // would only produce a ticket/email mismatch the backend
                    // then refuses.
                    readOnly={!!googleTicket}
                    {...register("email", { required: true })}
                  />
                </div>

                {/* Mobile number is kept on the administrator's account. */}
                <div>
                  <label
                    htmlFor="mobile"
                    className="rg-eyebrow text-[#8a8580] block mb-2"
                    style={{ fontSize: "10px" }}
                  >
                    Mobile number
                  </label>
                  <input
                    id="mobile"
                    type="tel"
                    inputMode="numeric"
                    placeholder="9876543210"
                    autoComplete="tel"
                    minLength={10}
                    maxLength={15}
                    pattern="[0-9]{10,15}"
                    className="rg-input"
                    style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                    {...register("mobile", {
                      required: true,
                      pattern: /^[0-9]{10,15}$/,
                    })}
                  />
                </div>

                {/* Company name — optional. When provided we pre-seed the
                    Company Info page so it appears automatically after login. */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="company_name"
                      className="rg-eyebrow text-[#8a8580]"
                      style={{ fontSize: "10px" }}
                    >
                      Company name
                    </label>
                    <span
                      className="rg-mono text-[#8a8580]"
                      style={{
                        fontSize: "9px",
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                      }}
                    >
                      Optional
                    </span>
                  </div>
                  <input
                    id="company_name"
                    type="text"
                    placeholder="Acme Pvt Ltd"
                    autoComplete="organization"
                    className="rg-input"
                    style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                    {...register("company_name")}
                  />
                </div>

                {!googleTicket && (
                  <>
                  {/* Password */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label
                        htmlFor="password"
                        className="rg-eyebrow text-[#8a8580]"
                        style={{ fontSize: "10px" }}
                      >
                        Password
                      </label>
                      {passwordValue && (
                        <span
                          className="rg-mono text-[#0f2d1f]"
                          style={{
                            fontSize: "10px",
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            opacity: strength.score >= 3 ? 1 : 0.7,
                            color: strength.score >= 3 ? "#2f9e6c" : strength.score >= 2 ? "#0f2d1f" : "#a86b2c",
                          }}
                        >
                          {strength.label}
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className="rg-input pr-10"
                        style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                        {...passwordRegister}
                        onChange={(e) => {
                          passwordRegister.onChange(e);
                          setPasswordValue(e.target.value);
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute right-1 top-3 text-[#8a8580] p-2 hover:text-[#0f2d1f] transition-colors"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>

                    {/* Strength bars */}
                    <div className="mt-3 flex items-center gap-1.5" aria-hidden>
                      {[0, 1, 2, 3].map((i) => {
                        const filled = passwordValue && strength.score > i;
                        const color =
                          strength.score >= 4
                            ? "#2f9e6c"
                            : strength.score === 3
                            ? "#0f2d1f"
                            : strength.score === 2
                            ? "#d4a356"
                            : "#a86b2c";
                        return (
                          <span
                            key={i}
                            className={filled ? "rg-bar" : ""}
                            style={{
                              flex: 1,
                              height: 3,
                              borderRadius: 2,
                              background: filled ? color : "#e8e3d8",
                              transition: "background 200ms",
                            }}
                          />
                        );
                      })}
                    </div>

                    <p className="text-[12px] text-[#8a8580] leading-[1.5] mt-3">
                      At least 8 characters. A mix of letters, numbers and a
                      symbol keeps things sturdy.
                    </p>
                  </div>
                  </>
                )}

                {/* Account type — text-only, restrained */}
                <input type="hidden" {...register("role")} />
                <div className="flex items-start gap-3 pt-1">
                  <div className="rg-num-rule h-px flex-1 mt-3" />
                  <div className="rg-mono text-[10px] text-[#8a8580] flex items-center gap-2 shrink-0" style={{ letterSpacing: "0.16em", textTransform: "uppercase" }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0f2d1f] rg-pulse" />
                    Role · Administrator
                  </div>
                  <div className="rg-num-rule h-px flex-1 mt-3" />
                </div>

                {/* Submit */}
                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0f2d1f] text-[#faf8f3] py-3.5 rounded-full font-medium rg-press hover:bg-[#0a1f15] disabled:opacity-60 disabled:cursor-not-allowed group"
                  >
                    {loading ? (
                      <>
                        <span className="w-4 h-4 rounded-full border-2 border-[#faf8f3] border-t-transparent animate-spin" />
                        <span>Setting up…</span>
                      </>
                    ) : (
                      <>
                        <span>Create account</span>
                        <ArrowRight className="w-3.5 h-3.5 rg-link-arrow" />
                      </>
                    )}
                  </button>
                </div>

                {/* Fine print */}
                <p className="text-[11px] text-[#8a8580] leading-[1.6] pt-1">
                  By continuing you agree to Teamlio's{" "}
                  <span className="text-[#0f2d1f] underline decoration-dotted underline-offset-2">
                    Terms
                  </span>{" "}
                  and{" "}
                  <span className="text-[#0f2d1f] underline decoration-dotted underline-offset-2">
                    Privacy notice
                  </span>
                  . We'll never sell what you write here.
                </p>
              </form>

              {/* Footer */}
              <div className="mt-10 pt-6 border-t border-dashed border-[#d6d0c2] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[13px]">
                <button
                  onClick={() => navigate("/")}
                  className="text-[#8a8580] hover:text-[#0f2d1f] transition-colors inline-flex items-center gap-1.5 group"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to home
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="text-[#0f2d1f] hover:text-[#1f4030] inline-flex items-center gap-1.5 group font-medium"
                >
                  Already with us? Sign in{" "}
                  <ArrowRight className="w-3 h-3 rg-link-arrow" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom strip */}
          <div className="relative px-6 lg:px-12 pb-6 flex items-center justify-between text-[#8a8580]">
            <div
              className="rg-mono"
              style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase" }}
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2f9e6c] rg-pulse" />
                Encrypted end-to-end
              </span>
            </div>
            <div
              className="rg-mono"
              style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase" }}
            >
              © {new Date().getFullYear()} Teamlio
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
